import React, { useCallback, useEffect, useState } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import { useLocation } from "react-router-dom";

import { api } from "../../api/api";
import { authAPI } from "../../api/authAPI";

import { authStore } from "./auth.store";
import { mapAuthResponseToUser, patchAuthUser, setSession } from "./auth.utils";

import { IAuthContextState, IAuthContextUser } from "./auth.types";

import { defaultAuthContextState, defaultAuthContextUser } from "./auth.state";

import { fetchAuthUserProfile, IAuthResponse } from "./auth.services";

import { ApiResponse } from "../../api/axios.types";

const AuthContext = React.createContext<IAuthContextState>(
  defaultAuthContextState,
);

export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userInfo, setUserInfo] = useState<IAuthContextUser>(
    defaultAuthContextUser,
  );
  const [loading, setLoading] = useState(true);

  //  central auth initializer
  const checkUserSession = useCallback(async () => {
    try {
      const storedAuth = authStore.load();
      if (!storedAuth) return setUserInfo(defaultAuthContextUser);
      setSession(storedAuth.token);
      setUserInfo((prev) => (prev.token ? prev : storedAuth));
      try {
        const profile = await fetchAuthUserProfile(storedAuth.user.user_uuid);
        setUserInfo((prev) => {
          const updated = patchAuthUser(prev, profile);
          //  keep localStorage synced
          authStore.save(updated);
          return updated;
        });
      } catch {
        // silent fail (keep stored data)
      }
    } catch (error) {
      console.error("Auth init failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  const login = async (
    email: string,
    password: string,
    onSuccess: (isOTPPreview: boolean) => void,
    onError: (message: string) => void,
  ) => {
    try {
      const res = await api.post<ApiResponse<IAuthResponse>>(authAPI.login, {
        email,
        password,
      });

      const data = res.data.data;

      if (data.isOTPPreview) {
        // store only email temporarily
        setUserInfo((prev) => ({
          ...prev,
          user: { ...prev.user, email },
        }));
      } else {
        const mappedUser = mapAuthResponseToUser(data);
        setUserInfo(mappedUser);
        authStore.save(mappedUser);
        checkUserSession();
      }
      onSuccess(data.isOTPPreview);
    } catch (error: any) {
      onError(error?.response?.data?.message || "Login failed");
    }
  };

  const verifyOTP = async (
    otp: string,
    onSuccess: () => void,
    onError: (message: string) => void,
  ) => {
    try {
      const res = await api.post<ApiResponse<IAuthResponse>>(
        authAPI.validateOtpGetToken,
        {
          email: userInfo.user.email,
          otp: Number(otp),
        },
      );

      const mappedUser = mapAuthResponseToUser(res.data.data);

      setUserInfo(mappedUser);
      authStore.save(mappedUser);
      checkUserSession();
      onSuccess();
    } catch (error: any) {
      onError(error?.response?.data?.message || "OTP verification failed");
    }
  };

  const logout = () => {
    authStore.clear();
    setUserInfo(defaultAuthContextUser);

    // 🔥 global redirect
    window.location.href = "/auth/login";
  };

  const addUserName = (name: string) => {
    setUserInfo((prev) => ({ ...prev, name }));
  };

  if (loading) return <LinearProgress />;

  return (
    <AuthContext.Provider
      value={{
        userInfo,
        isLogin: !!userInfo.token,
        login,
        verifyOTP,
        logout,
        addUserName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
